/**
 * @summary - Component that Provides Custom DatePicker Functionality
 * @autor vengateshwaran k
 * @since 2024-06-21
 */
import React, { useState, useMemo, useRef } from "react";
import {
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const CustomDatePicker = ({
  label,
  id,
  value,
  onDateChange,
  formatCondition = true,
  isMandatoryField = false,
  isStatrtDate = false,
  dissablePastDate = false,
  minimumDate,
  disabled,
  error,
  helperText,
  maxDate,
  isRefilByOtherField = false,
  autoFocus = false,
  errorMessage,
  name,
  modal,
}) => {
  const [selectValue, setSelectValue] = useState(null);
  const [open, setOpen] = useState(false);
  const minimumDates = minimumDate ? dayjs(minimumDate, "DD/MM/YYYY") : null;
  const maxdates = maxDate ? dayjs(maxDate, "DD/MM/YYYY") : null;

  const mandatoryRequiredButton = useSelector(
    (state) => state.mandatoryFields.isMandatoryRequiredEnabled
  );
  const viewEnabled = useSelector(
    (state) => state.mandatoryFields.isViewEnabled
  );

  useMemo(() => {
    if (value) {
      const parsedDate = dayjs(value, "DD/MM/YYYY");
      setSelectValue(parsedDate.isValid() ? parsedDate : null);
    } else {
      setSelectValue(null);
    }
  }, [value]);

  const handleDateChange = (date) => {
    setSelectValue(date);
    const formattedDate = date.format("DD/MM/YYYY");
    onDateChange(formattedDate, name, isMandatoryField);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Typography variant="labelSm" color={"text.primary"} component={"label"}>
        {label}
        {isMandatoryField && (
          <Typography component={"span"} color={"error"}>
            *
          </Typography>
        )}
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          autoFocus={autoFocus}
          onOpen={() => setOpen(true)}
          id={id}
          value={selectValue}
          open={open}
          onClose={handleClose}
          onChange={handleDateChange}
          format="DD/MM/YYYY"
          type="datetime-local"
          sx={{ width: "100%" }}
          disablePast={dissablePastDate}
          minDate={minimumDate && minimumDates.isValid() ? minimumDates : null}
          maxDate={maxDate && maxdates.isValid() ? maxdates : null}
          name={name}
          disabled={
            viewEnabled ||
            (mandatoryRequiredButton && !isMandatoryField) ||
            disabled ||
            isRefilByOtherField
          }
          slotProps={{
            textField: {
              helperText: helperText,
              error: error,
              readOnly: true, 
              InputProps: {
                placeholder: disabled ? "" : "DD/MM/YYYY",
              },
              onClick: () => setOpen(true),
            },
            popper: {
              className: modal && "zIndex-1400",
            },
          }}
        />
      </LocalizationProvider>
      {error && (
 <FormHelperText sx={{ color: "#d9534f", borderColor: "#d9534f" }}>
 {errorMessage}
</FormHelperText>
      )}
    </>
  );
};

export default CustomDatePicker;
